import { ProductRenderer } from '../ProductRenderer';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { debounce } from 'lodash';
import { ThreeEvent } from '../types';
import { PerspectiveCamera, Vector3, Box3, MathUtils } from 'three';
import RENDERER_CONFIG from '@/configs/rendererConfig';
import gsap from 'gsap';

export class CameraController {
	camera!: PerspectiveCamera;
	screenshotCamera!: PerspectiveCamera;
	controls!: OrbitControls;

	constructor(private renderer: ProductRenderer) {
		this.init();
	}

	private init() {
		this.createCameras();
		this.createControls();
	}

	private createCameras() {
		this.camera = new PerspectiveCamera();
		this.screenshotCamera = new PerspectiveCamera();

		for (const camera of [this.camera, this.screenshotCamera]) {
			const aspect =
				this.renderer.canvas!.clientWidth / this.renderer.canvas!.clientHeight;

			camera.layers.enableAll();
			camera.aspect = aspect;
			camera.position.set(0, 2, 5);

			camera.updateProjectionMatrix();
		}
	}

	private createControls() {
		this.controls = new OrbitControls(this.camera, this.renderer.canvas);
		this.controls.target.set(0, 0, 0);

		this.controls.maxDistance = RENDERER_CONFIG.CAMERA.MAX_DISTANCE;
		this.controls.minDistance = RENDERER_CONFIG.CAMERA.MIN_DISTANCE;

		this.controls.enablePan = import.meta.env.DEV;

		if (import.meta.env.PROD)
			this.controls.maxPolarAngle = MathUtils.degToRad(100);

		this.controls.enableDamping = true;
		this.controls.dampingFactor = 0.05;

		this.controls.addEventListener(
			'change',
			debounce(() => {
				const distance = this.camera.position.distanceTo(this.controls.target);

				this.renderer.emitter.emit(ThreeEvent.DISTANCE, distance);
			}, 150)
		);

		this.controls.saveState();
	}

	prepareScreenshotCamera() {
		const box = new Box3().setFromObject(this.renderer.product);
		const size = new Vector3();
		box.getSize(size);

		const aspect =
			RENDERER_CONFIG.SCREENSHOT.WIDTH / RENDERER_CONFIG.SCREENSHOT.HEIGHT;
		this.screenshotCamera.aspect = aspect;
		this.screenshotCamera.updateProjectionMatrix();

		const center = box.getCenter(new Vector3());

		const position = new Vector3(center.x, size.y * 0.5, size.y * 1.5);

		this.screenshotCamera.position.copy(position);
		this.screenshotCamera.lookAt(center);
	}

	centerCamera() {
		const box = new Box3().setFromObject(this.renderer.product);
		const size = new Vector3();
		box.getSize(size);

		const center = box.getCenter(new Vector3());

		this.controls.target.set(...center.toArray());

		gsap.to(this.camera.position, {
			x: center.x,
			y: size.y * 0.5,
			z: size.y * 1.5,
			duration: 0.5,
			ease: 'power1.inOut',
			onUpdate: () => {
				this.controls.update();
			},
			onComplete: () => {},
		});
	}

	handleCameraZoom(zoom: number) {
		const target = this.controls.target.clone();
		const currentPosition = this.camera.position.clone();
		const direction = new Vector3()
			.subVectors(currentPosition, target)
			.normalize();

		const newPosition = target
			.clone()
			.add(direction.clone().multiplyScalar(zoom));

		gsap.to(this.camera.position, {
			x: newPosition.x,
			y: newPosition.y,
			z: newPosition.z,
			duration: 0.5,
			ease: 'power1.inOut',
			onUpdate: () => {
				this.camera.lookAt(target);
			},
			onComplete: () => {
				this.controls.update();
			},
		});
	}

	handleResize(aspect: number) {
		this.camera.aspect = aspect;
		this.camera.updateProjectionMatrix();
	}

	dispose() {
		this.controls.dispose();
	}
}
